import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, Receipt, Banknote } from "lucide-react";
import { StatusPill } from "@/components/data-table/StatusPill";
import { projectStatusColors } from "@/pages/projects/config";
import { formatCompactCurrency, formatDateForDisplay } from "@/lib/formatting";
import { InvoiceSidePanel } from "./InvoiceSidePanel";
import { hasOverdueCosts, hasUnpaidRevenues, getTotalCostsForProject, getPaidRevenueForProject, getProfitForProject, getProfitMarginForProject } from "./invoiceUtils";
import type { Project, Cost, Revenue, CostMpkAllocation } from "@shared/schema";

interface ProjectDrilldownTableProps {
  projects: Project[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  costs?: Cost[];
  revenues?: Revenue[];
  mpkAllocations?: CostMpkAllocation[];
}

interface SidePanelState {
  isOpen: boolean;
  projectNr: string;
  projectName: string | null;
  panelType: "costs" | "revenues";
}

export function ProjectDrilldownTable({
  projects,
  currentPage,
  totalPages,
  onPageChange,
  costs = [],
  revenues = [],
  mpkAllocations = [],
}: ProjectDrilldownTableProps) {
  const [sidePanelState, setSidePanelState] = useState<SidePanelState | null>(null);

  const hasInvoiceData = costs.length > 0 || revenues.length > 0;

  const handleOpenCosts = (project: Project) => {
    setSidePanelState({
      isOpen: true,
      projectNr: project.nrPorzadkowy,
      projectName: project.nazwaRoboczaProjektu,
      panelType: "costs",
    });
  };

  const handleOpenRevenues = (project: Project) => {
    setSidePanelState({
      isOpen: true,
      projectNr: project.nrPorzadkowy,
      projectName: project.nazwaRoboczaProjektu,
      panelType: "revenues",
    });
  };

  const handleCloseSidePanel = () => {
    setSidePanelState(null);
  };

  // Calculate totals for summary row
  const totals = projects.reduce(
    (acc, project) => {
      const contractValue = parseFloat(project.wartoscUmowyNetto || "0") || 0;
      const costs = getTotalCostsForProject(project.nrPorzadkowy, mpkAllocations);
      const revenue = getPaidRevenueForProject(project.nrPorzadkowy, revenues);
      const profit = revenue - costs;

      return {
        contractValue: acc.contractValue + contractValue,
        costs: acc.costs + costs,
        revenue: acc.revenue + revenue,
        profit: acc.profit + profit,
      };
    },
    { contractValue: 0, costs: 0, revenue: 0, profit: 0 }
  );

  const totalMargin = totals.revenue > 0 ? (totals.profit / totals.revenue) * 100 : null;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Nr</TableHead>
            <TableHead className="w-[200px]">Nazwa projektu</TableHead>
            <TableHead className="w-[180px]">Klient</TableHead>
            <TableHead className="w-[130px]">Status</TableHead>
            <TableHead className="w-[100px] text-right">Wartość</TableHead>
            {hasInvoiceData && (
              <>
                <TableHead className="w-[90px] text-right">Koszty</TableHead>
                <TableHead className="w-[90px] text-right">Przychód</TableHead>
                <TableHead className="w-[90px] text-right">Zysk</TableHead>
                <TableHead className="w-[70px] text-right">Marża</TableHead>
              </>
            )}
            <TableHead className="w-[130px]">Data podpisania</TableHead>
            {hasInvoiceData && (
              <TableHead className="w-[100px] text-center">Faktury</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((project) => {
            const projectHasOverdueCosts = hasOverdueCosts(
              project.nrPorzadkowy,
              costs,
              mpkAllocations
            );
            const projectHasUnpaidRevenues = hasUnpaidRevenues(
              project.nrPorzadkowy,
              revenues
            );
            const totalCosts = getTotalCostsForProject(project.nrPorzadkowy, mpkAllocations);
            const paidRevenue = getPaidRevenueForProject(project.nrPorzadkowy, revenues);
            const profit = getProfitForProject(project.nrPorzadkowy, revenues, mpkAllocations);
            const profitMargin = getProfitMarginForProject(project.nrPorzadkowy, revenues, mpkAllocations);

            return (
              <TableRow key={project.id}>
                <TableCell className="font-mono">{project.nrPorzadkowy}</TableCell>
                <TableCell>{project.nazwaRoboczaProjektu || "-"}</TableCell>
                <TableCell>{project.klient || "-"}</TableCell>
                <TableCell>
                  <StatusPill
                    value={project.statusProjektu || ""}
                    colorMapping={projectStatusColors}
                    testIdType="projectStatus"
                  />
                </TableCell>
                <TableCell className="text-right font-mono">
                  {formatCompactCurrency(project.wartoscUmowyNetto)}
                </TableCell>
                {hasInvoiceData && (
                  <>
                    <TableCell className="text-right font-mono text-red-600">
                      {totalCosts > 0 ? formatCompactCurrency(totalCosts) : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {paidRevenue > 0 ? formatCompactCurrency(paidRevenue) : "-"}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {totalCosts > 0 || paidRevenue > 0 ? formatCompactCurrency(profit) : "-"}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${profitMargin !== null && profitMargin >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {profitMargin !== null ? `${profitMargin.toFixed(1)}%` : "-"}
                    </TableCell>
                  </>
                )}
                <TableCell>{formatDateForDisplay(project.dataPodpisaniaUmowy)}</TableCell>
                {hasInvoiceData && (
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 relative"
                              onClick={() => handleOpenCosts(project)}
                              aria-label="Pokaż koszty"
                            >
                              <Receipt className="h-4 w-4" />
                              {projectHasOverdueCosts && (
                                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-red-500 rounded-full" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Pokaż koszty</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 relative"
                              onClick={() => handleOpenRevenues(project)}
                              aria-label="Pokaż przychody"
                            >
                              <Banknote className="h-4 w-4" />
                              {projectHasUnpaidRevenues && (
                                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-red-500 rounded-full" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Pokaż przychody</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {projects.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={hasInvoiceData ? 11 : 6}
                className="text-center text-muted-foreground py-8"
              >
                Brak projektów w tej kategorii
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {projects.length > 0 && (
          <TableFooter>
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell colSpan={4} className="text-right">
                Suma ({projects.length} {projects.length === 1 ? "projekt" : projects.length < 5 ? "projekty" : "projektów"})
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCompactCurrency(totals.contractValue)}
              </TableCell>
              {hasInvoiceData && (
                <>
                  <TableCell className="text-right font-mono text-red-600">
                    {totals.costs > 0 ? formatCompactCurrency(totals.costs) : "-"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-green-600">
                    {totals.revenue > 0 ? formatCompactCurrency(totals.revenue) : "-"}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${totals.profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {totals.costs > 0 || totals.revenue > 0 ? formatCompactCurrency(totals.profit) : "-"}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${totalMargin !== null && totalMargin >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {totalMargin !== null ? `${totalMargin.toFixed(1)}%` : "-"}
                  </TableCell>
                </>
              )}
              <TableCell />
              {hasInvoiceData && <TableCell />}
            </TableRow>
          </TableFooter>
        )}
      </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <p className="text-sm text-muted-foreground">
            Strona {currentPage} z {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Poprzednia
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Następna
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {sidePanelState && (
        <InvoiceSidePanel
          isOpen={sidePanelState.isOpen}
          onClose={handleCloseSidePanel}
          projectNr={sidePanelState.projectNr}
          projectName={sidePanelState.projectName}
          panelType={sidePanelState.panelType}
          costs={costs}
          revenues={revenues}
          mpkAllocations={mpkAllocations}
        />
      )}
    </div>
  );
}
